import { collection, doc, getDoc, getDocs, getFirestore, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { CONT_COLLECTIONS } from "./collections";
import { SIRE_ESTADOS } from "./sireEstados";
import { actualizarEstadoSirePeriodo } from "./sirePeriodoService";
import { obtenerConfiguracionSire } from "./sireService";
import { consultarTicketSireApi, obtenerTokenSunat, presentarArchivoSireApi } from "./sunatSireProvider";
import { obtenerAutorizacionPresentacion } from "./sireAutorizacionService";

const db = getFirestore(firebaseApp);

const cleanText = (v) => (v == null ? "" : String(v).trim());
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const presentarArchivoSire = async ({ rucEmpresa, periodo, tipo, archivoObj, usuario }) => {
  const cleanRuc = cleanText(rucEmpresa);
  const cleanPer = cleanText(periodo);
  const cleanTip = cleanText(tipo).toUpperCase();

  if (!archivoObj) {
    throw new Error("No se proporcionó un objeto de archivo para la presentación.");
  }

  // Regla 4: Solamente permitir si archivo.valido === true, hash_sha256 existe, propuesta_sunat_id existe
  if (!archivoObj.valido && archivoObj.valido !== undefined) {
    throw new Error("El archivo seleccionado contiene errores de prevalidación y no puede ser presentado a SUNAT.");
  }

  if (!archivoObj.hash_sha256) {
    throw new Error("El archivo no cuenta con un Hash SHA-256 válido.");
  }

  if (!archivoObj.propuesta_sunat_id) {
    throw new Error("El archivo no tiene vinculada una propuesta SUNAT de origen.");
  }

  const hashKey = cleanText(archivoObj.hash_sha256);

  // FASE 21: CONTROL DE AUTORIZACIÓN EN BACKEND
  const auth = await obtenerAutorizacionPresentacion(cleanRuc, cleanPer, cleanTip, hashKey);
  if (!auth || auth.estado !== "AUTORIZADO") {
    throw new Error("PRESENTACION_NO_AUTORIZADA: No existe una autorización activa para este archivo contable.");
  }

  const archivoId = cleanText(archivoObj.id || `${cleanRuc}_${cleanPer}_${cleanTip}_${String(archivoObj.version || 1).padStart(3, "0")}`);
  const presentacionId = `${cleanRuc}_${cleanPer}_${cleanTip}_${hashKey}`;

  // FASE 21: CLAVE DETERMINISTA DE IDEMPOTENCIA
  const presDocRef = doc(db, CONT_COLLECTIONS.SIRE_PRESENTACIONES, presentacionId);
  const presSnap = await getDoc(presDocRef);
  if (presSnap.exists()) {
    const p = presSnap.data();
    if (["ENVIANDO", "ENVIADO", "PROCESANDO", "ACEPTADO"].includes(p.estado)) {
      throw new Error(`Este archivo ya tiene una presentación registrada ante SUNAT (Estado: ${p.estado}, Ticket: ${p.ticket || "N/A"}).`);
    }
  }

  // Obtener número de intentos para el historial técnico
  const qIntentos = query(
    collection(db, CONT_COLLECTIONS.SIRE_INTENTOS),
    where("archivo_id", "==", archivoId)
  );
  const snapIntentos = await getDocs(qIntentos);
  const intento = snapIntentos.docs.length + 1;
  const intentoId = `${presentacionId}_I${intento}`;
  const intentoRef = doc(db, CONT_COLLECTIONS.SIRE_INTENTOS, intentoId);

  // Registrar inicio del intento técnico
  await setDoc(intentoRef, {
    ruc_empresa: cleanRuc,
    periodo: cleanPer,
    tipo: cleanTip,
    archivo_id: archivoId,
    hash_sha256: hashKey,
    tipo_operacion: "ENVIO_SUNAT",
    estado: "INICIADO",
    usuario: cleanText(usuario),
    timestamp: serverTimestamp(),
  });

  // Transición a ENVIANDO
  await actualizarEstadoSirePeriodo({
    rucEmpresa: cleanRuc,
    periodo: cleanPer,
    tipo: cleanTip,
    nuevoEstado: SIRE_ESTADOS.ENVIANDO,
  });

  // Autenticación con SUNAT
  let token = null;
  const cfg = await obtenerConfiguracionSire(cleanRuc);
  if (cfg && cfg.clientId && cfg.clientSecret && cfg.usuarioSol && cfg.claveSol) {
    const authRes = await obtenerTokenSunat({
      ruc: cleanRuc,
      clientId: cfg.clientId,
      clientSecret: cfg.clientSecret,
      usuarioSol: cfg.usuarioSol,
      claveSol: cfg.claveSol,
    });
    if (authRes.success) token = authRes.token;
  }

  // Envío de presentación a SUNAT
  const envRes = await presentarArchivoSireApi({
    ruc: cleanRuc,
    periodo: cleanPer,
    tipo: cleanTip,
    token: token || "MOCK_TOKEN",
    payloadArchivo: archivoObj,
  });

  if (!envRes.success) {
    await setDoc(intentoRef, { estado: "FALLIDO", error: envRes.message || "Error" }, { merge: true });
    await actualizarEstadoSirePeriodo({
      rucEmpresa: cleanRuc,
      periodo: cleanPer,
      tipo: cleanTip,
      nuevoEstado: SIRE_ESTADOS.ERROR,
    });
    throw new Error(envRes.message || "No fue posible comunicarse con SUNAT.");
  }

  const ticket = envRes.ticket || `TKT-${cleanPer}-${Date.now().toString().slice(-6)}`;

  // Actualizar intento técnico a completado
  await setDoc(intentoRef, { estado: "COMPLETADO", ticket }, { merge: true });

  const dataPayload = {
    ruc_empresa: cleanRuc,
    periodo: cleanPer,
    tipo: cleanTip,
    archivo_id: archivoId,
    version: toNum(archivoObj.version || 1),
    nombre_archivo: cleanText(archivoObj.nombre_archivo || `LE${cleanRuc}${cleanPer.replace(/-/g, "")}00140400001111.txt`),
    hash_sha256: hashKey,
    propuesta_sunat_id: cleanText(archivoObj.propuesta_sunat_id),
    origen: cleanText(archivoObj.propuesta_origen || "API_SUNAT_REAL"),
    estado: SIRE_ESTADOS.ENVIADO,
    ticket,
    codigo_respuesta: cleanText(envRes.codRespuesta || "0"),
    mensaje_respuesta: cleanText(envRes.message || "Ticket de presentación generado."),
    enviado_at: serverTimestamp(),
    enviado_por: cleanText(usuario),
    autorizacion_id: auth.id,
  };

  await setDoc(presDocRef, dataPayload);

  // Transición a ENVIADO
  await actualizarEstadoSirePeriodo({
    rucEmpresa: cleanRuc,
    periodo: cleanPer,
    tipo: cleanTip,
    nuevoEstado: SIRE_ESTADOS.ENVIADO,
    metadata: {
      archivo_enviado: true,
    },
  });

  return { id: presentacionId, ...dataPayload };
};

export const consultarResultadoPresentacion = async ({ rucEmpresa, periodo, tipo, presentacionId, ticket }) => {
  const cleanRuc = cleanText(rucEmpresa);
  const cleanPer = cleanText(periodo);
  const cleanTip = cleanText(tipo).toUpperCase();
  const cleanPresId = cleanText(presentacionId);

  const presDocRef = doc(db, CONT_COLLECTIONS.SIRE_PRESENTACIONES, cleanPresId);
  const presSnap = await getDoc(presDocRef);

  let presData = presSnap.exists() ? presSnap.data() : {};
  const cleanTkt = cleanText(ticket || presData.ticket);

  let token = null;
  const cfg = await obtenerConfiguracionSire(cleanRuc);
  if (cfg && cfg.clientId && cfg.clientSecret && cfg.usuarioSol && cfg.claveSol) {
    const authRes = await obtenerTokenSunat({
      ruc: cleanRuc,
      clientId: cfg.clientId,
      clientSecret: cfg.clientSecret,
      usuarioSol: cfg.usuarioSol,
      claveSol: cfg.claveSol,
    });
    if (authRes.success) token = authRes.token;
  }

  const queryRes = await consultarTicketSireApi({
    ruc: cleanRuc,
    periodo: cleanPer,
    tipo: cleanTip,
    token: token || "MOCK_TOKEN",
    ticket: cleanTkt,
  });

  let nuevoEstadoPeriodo = SIRE_ESTADOS.ACEPTADO;
  const estadoSunatUpper = cleanText(queryRes.estado).toUpperCase();

  if (estadoSunatUpper.includes("OBSERVA")) {
    nuevoEstadoPeriodo = SIRE_ESTADOS.OBSERVADO;
  } else if (estadoSunatUpper.includes("RECHAZA") || estadoSunatUpper.includes("ERROR")) {
    nuevoEstadoPeriodo = SIRE_ESTADOS.RECHAZADO;
  } else {
    nuevoEstadoPeriodo = SIRE_ESTADOS.ACEPTADO;
  }

  const updatePayload = {
    estado: nuevoEstadoPeriodo,
    codigo_respuesta: cleanText(queryRes.codEstado || "00"),
    mensaje_respuesta: cleanText(queryRes.message || `Respuesta SUNAT: ${nuevoEstadoPeriodo}`),
    errores: queryRes.errores || [],
    actualizado_at: serverTimestamp(),
  };

  if (presSnap.exists()) {
    await setDoc(presDocRef, updatePayload, { merge: true });
  }

  await actualizarEstadoSirePeriodo({
    rucEmpresa: cleanRuc,
    periodo: cleanPer,
    tipo: cleanTip,
    nuevoEstado: nuevoEstadoPeriodo,
    metadata: {
      archivo_aceptado: nuevoEstadoPeriodo === SIRE_ESTADOS.ACEPTADO,
    },
  });

  return { id: cleanPresId, ticket: cleanTkt, estado: nuevoEstadoPeriodo, ...updatePayload };
};

export const obtenerPresentacionesSire = async ({ rucEmpresa, periodo, tipo }) => {
  const cleanRuc = cleanText(rucEmpresa);
  const cleanPer = cleanText(periodo);
  const cleanTip = cleanText(tipo).toUpperCase();

  const q = query(
    collection(db, CONT_COLLECTIONS.SIRE_PRESENTACIONES),
    where("ruc_empresa", "==", cleanRuc),
    where("periodo", "==", cleanPer),
    where("tipo", "==", cleanTip)
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};
